import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { revenueApi, type PaginatedRevenueStatementsResponse } from "@/lib/api";
import type { RevenueStatement } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";
import { DEFAULT_PAGE_SIZE } from "@/lib/constants";
import { POLLING_INTERVAL } from "@/hooks/useInterval";
import { isStatementBusy } from "@/lib/revenue";

interface UseRevenueStatementsOptions {
  enabled?: boolean;
  search?: string;
  pageSize?: number;
  page?: number;
  settled?: boolean;
  vendors?: string[];
  ordering?: string;
}

interface UseRevenueStatementsReturn {
  statements: RevenueStatement[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createRevenueStatementsQueryKey = (
  labelId: string | undefined,
  search: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  settled: boolean | undefined,
  vendors: string[] | undefined,
  ordering: string | undefined,
) => {
  const key: (string | number)[] = ["revenue-statements"];

  // Always include labelId (required for most queries)
  if (labelId) key.push("label", labelId);

  // Normalize search - convert undefined/empty to consistent value
  const normalizedSearch = search?.trim() || null;
  if (normalizedSearch) key.push("search", normalizedSearch);

  // Add pageSize if specified and different from default
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);

  if (page && page > 1) key.push("page", page);

  // Add filter parameters
  if (settled !== undefined) key.push("settled", String(settled));
  if (vendors && vendors.length > 0) key.push("vendors", [...vendors].sort().join(","));
  if (ordering) key.push("ordering", ordering);

  return key;
};

export const useRevenueStatements = ({
  enabled = true,
  search,
  pageSize,
  page,
  settled,
  vendors,
  ordering,
}: UseRevenueStatementsOptions = {}): UseRevenueStatementsReturn => {
  const { currentLabel } = useOrganizationStore();

  const filters = {
    label_id: currentLabel?.id,
    ...(search && { q: search }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(settled !== undefined && { is_settled: settled ? "true" : "false" }),
    ...(vendors && vendors.length > 0 && { vendor: vendors.join(",") }),
    ...(ordering && { ordering }),
  };

  // Use normalized query key
  const queryKey = createRevenueStatementsQueryKey(
    currentLabel?.id,
    search,
    pageSize,
    page,
    settled,
    vendors,
    ordering,
  );

  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedRevenueStatementsResponse> => {
      return await revenueApi.getRevenueStatements(filters);
    },
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
    // Amount received depends on ledger processing that finishes after the
    // import batch reports complete, and settlement books in a background
    // task — keep polling until every visible statement reaches a terminal
    // status.
    refetchInterval: (query) =>
      query.state.data?.results.some((s) => isStatementBusy(s))
        ? POLLING_INTERVAL
        : false,
  });

  return {
    statements: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
